import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Spinner,
  Card,
  Row,
  Col,
  Alert,
  Carousel,
  Modal,
  Form,
  Button,
} from "react-bootstrap";
import axios from "axios";
import Layout from "../../layout/default";
import Swal from "sweetalert2";
import { FaMapPin } from "react-icons/fa";
import DOMPurify from "dompurify";

const AddUserAddressModal = ({ show, onHide, onSave }) => {
  const [formData, setFormData] = useState({
    state_id: "",
    district_id: "",
    city_id: "",
    zone_id: "",
    address: "",
    billing_address: "",
    billing_same_as_shipping: true,
    shipping_phone_number: "",
    billing_phone_number: "",
    shipping_pincode: "",
    billing_pincode: "",
    billing_state_id: "",
    billing_district_id: "",
    billing_city_id: "",
  });

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingZones, setLoadingZones] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Prepare the data based on whether billing is same as shipping
    const billingSameAsShipping = formData.billing_same_as_shipping;

    // Validate required fields
    if (!formData.address || (!billingSameAsShipping && !formData.billing_address)) {
      alert(billingSameAsShipping
        ? "Please provide address."
        : "Please provide address.");
      setLoading(false);
      return;
    }

    // Prepare the data object
    const formattedData = {
      billing_address: {
        address: billingSameAsShipping ? formData.address : formData.billing_address,
        phone_number: billingSameAsShipping ? formData.shipping_phone_number : formData.billing_phone_number,
        state_id: billingSameAsShipping ? formData.state_id : formData.billing_state_id,
        district_id: billingSameAsShipping ? formData.district_id : formData.billing_district_id,
        city_id: billingSameAsShipping ? formData.city_id : formData.billing_city_id,
        pincode: billingSameAsShipping ? formData.shipping_pincode : formData.billing_pincode,
        ...(billingSameAsShipping
          ? (formData.zone_id ? { zone_id: formData.zone_id } : {})
          : (formData.billing_zone_id ? { zone_id: formData.billing_zone_id } : {}))
      },
      shipping_address: {
        address: formData.address,
        phone_number: formData.shipping_phone_number,
        state_id: formData.state_id,
        district_id: formData.district_id,
        city_id: formData.city_id,
        pincode: formData.shipping_pincode,
        ...(formData.zone_id ? { zone_id: formData.zone_id } : {})
      }
    };

    try {
      const authToken = localStorage.getItem("authToken");
      await axios.post(
        `${process.env.REACT_APP_HOST}api/address`,
        formattedData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      await Swal.fire({
        imageUrl: "/shop/images/Success2.svg",
        imageWidth: '100%',
        imageHeight: 'auto',
        imageAlt: "Address created successfully",
        text: "Address created successfully",
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false,
        willClose: () => {
          onSave(); // Call this after the alert closes
        },
        customClass: {
          image: 'full-size-image'
        }
      });
    } catch (error) {
      console.error("API Error:", error);
      alert("Failed to save address. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDistricts = async (stateId) => {
    if (!stateId) {
      setDistricts([]);
      return;
    }
    try {
      const authToken = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_HOST}api/state_districts?state_id=${stateId}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      setDistricts(response.data.data || []);
    } catch (error) {
      console.error("Failed to fetch districts:", error);
    }
  };

  const fetchCities = async (districtId) => {
    if (!districtId) {
      setCities([]);
      return;
    }
    try {
      const authToken = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_HOST}api/district_cities?district_id=${districtId}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      setCities(response.data.data || []);
    } catch (error) {
      console.error("Failed to fetch cities:", error);
    }
  };

  const fetchZones = async (cityId) => {
    if (!cityId) {
      setZones([]);
      return;
    }
    try {
      const authToken = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_HOST}api/city_zones?city_id=${cityId}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      setZones(response.data.data || []);
    } catch (error) {
      console.error("Failed to fetch zones:", error);
    }
  };

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_HOST}api/states`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => setStates(response.data))
      .catch((error) =>
        Swal.fire(
          "warning!",
          "Failed to load states, please login again",
          "error"
        )
      );
  }, []);

  const authToken = localStorage.getItem("authToken");
  useEffect(() => {
    if (formData.state_id) {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        Swal.fire("Error!", "You need to log in first.", "error");
        return;
      }

      axios
        .get(
          `${process.env.REACT_APP_HOST}api/state_districts?state_id=${formData.state_id}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        )
        .then((response) => setDistricts(response.data.data || []))
        .catch((error) =>
          Swal.fire("Error!", "Failed to fetch districts.", "error")
        );
    } else {
      setDistricts([]);
    }
  }, [formData.state_id]);

  useEffect(() => {
    if (formData.district_id) {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        Swal.fire("Error!", "You need to log in first.", "error");
        return;
      }

      axios
        .get(
          `${process.env.REACT_APP_HOST}api/district_cities?district_id=${formData.district_id}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        )
        .then((response) => setCities(response.data.data || []))
        .catch((error) =>
          Swal.fire("Error!", "Failed to fetch cities.", "error")
        );
    } else {
      setCities([]);
    }
  }, [formData.district_id]);

  useEffect(() => {
    if (!formData.city_id) {
      setZones([]);
      return;
    }

    setLoadingZones(true);
    axios
      .get(
        `${process.env.REACT_APP_HOST}api/city_zones?city_id=${formData.city_id}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then((res) => {
        setZones(res.data.data || []);
      })
      .catch((error) => {
        // Silently handle 404 (no zones) - don't show error
        if (error.response?.status !== 404) {
          Swal.fire("Error!", "Failed to fetch zones.", "error");
        }
        setZones([]);
      })
      .finally(() => {
        setLoadingZones(false);
      });
  }, [formData.city_id]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Address Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col-12 d-lg-none text-center mb-3"> {/* Mobile image - full width */}
            <img
              src="/shop/images/Add-Address.svg"
              alt="Add Address"
            />
          </div>
          {/* Image column - hidden on mobile, shown on desktop */}
          <div className="col-lg-4 d-none d-lg-flex align-items-center justify-content-center">
            <img
              src="/shop/images/Add-Address.svg"
              alt="Add Address"
            />
          </div>
          <div className="col-lg-8">
            <Form onSubmit={handleSubmit}>
              <h5 className="text-success mb-3">Billing Address</h5>

              <Form.Group className="mb-3">
                <Form.Label>Address</Form.Label>
                <Form.Control
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                />
              </Form.Group>

              <Row>
                {/* Shipping Phone Number */}
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone Number</Form.Label>
                    <Form.Control
                      type="tel"
                      name="shipping_phone_number"
                      value={formData.shipping_phone_number || ""}
                      onChange={(e) => {
                        const value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 10);
                        setFormData({ ...formData, shipping_phone_number: value });
                      }}
                      required
                      pattern="[0-9]{10}"
                      maxLength="10"
                      onBlur={(e) => {
                        if (e.target.value.length !== 10) {
                          Swal.fire({
                            icon: 'none',
                            imageUrl: "/shop/images/Failed_1.svg",
                            imageWidth: '100%',
                            imageHeight: 'auto',
                            imageAlt: "Error",
                            title: "Invalid Phone number",
                            text: "phone number must be exactly 10 digits",
                            confirmButtonColor: "#3085d6",
                            confirmButtonText: "OK",
                          });
                        }
                      }}
                    />
                  </Form.Group>
                </Col>

                {/* Pincode */}
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Pincode</Form.Label>
                    <Form.Control
                      type="text"
                      name="shipping_pincode"
                      value={formData.shipping_pincode || ""}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col>
                  <Form.Group>
                    <Form.Label>State</Form.Label>
                    <Form.Select
                      name="state_id"
                      value={formData.state_id}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select State</option>
                      {states.map((state) => (
                        <option key={state.id} value={state.id}>
                          {state.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>District</Form.Label>
                    <Form.Select
                      name="district_id"
                      value={formData.district_id}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select District</option>
                      {districts.map((district) => (
                        <option key={district.id} value={district.id}>
                          {district.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col>
                  <Form.Group>
                    <Form.Label>City</Form.Label>
                    <Form.Select
                      name="city_id"
                      value={formData.city_id}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select City</option>
                      {cities.map((city) => (
                        <option key={city.id} value={city.id}>
                          {city.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                {/* Only show zone field if zones exist for this city */}
                {zones.length > 0 && (
                  <Col>
                    <Form.Group>
                      <Form.Label>
                        <FaMapPin className="me-2" /> Zone (Optional)
                      </Form.Label>
                      <Form.Select
                        name="zone_id"
                        value={formData.zone_id}
                        onChange={handleChange}
                      >
                        <option value="">Select Zone</option>
                        {zones.map((z) => (
                          <option key={z.id} value={z.id}>
                            {z.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}
              </Row>

              <Form.Check
                type="checkbox"
                label="Permanent address is same as billing address"
                name="billing_same_as_shipping"
                className="mb-3"
                checked={formData.billing_same_as_shipping}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    billing_same_as_shipping: e.target.checked,
                    billing_address: e.target.checked ? formData.address : "",
                    billing_phone_number: e.target.checked
                      ? formData.shipping_phone_number
                      : "",
                    billing_pincode: e.target.checked
                      ? formData.shipping_pincode
                      : "",
                    billing_state_id: e.target.checked ? formData.state_id : "",
                    billing_district_id: e.target.checked
                      ? formData.district_id
                      : "",
                    billing_city_id: e.target.checked ? formData.city_id : "",
                    billing_zone_id: e.target.checked ? formData.zone_id : "",
                  })
                }
              />

              {!formData.billing_same_as_shipping && (
                <>
                  <h5 className="text-info mt-4 mb-3">Permanent Address</h5>

                  <Form.Group className="mb-3">
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                      type="text"
                      name="billing_address"
                      value={formData.billing_address}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  {/* Billing Phone Number - Only show if different from shipping */}
                  {!formData.billing_same_as_shipping && (
                    <Row className="mb-3">
                      <Col>
                        <Form.Group>
                          <Form.Label>Phone Number</Form.Label>
                          <Form.Control
                            type="tel"
                            name="billing_phone_number"
                            value={formData.billing_phone_number || ""}
                            onChange={(e) => {
                              const value = e.target.value
                                .replace(/\D/g, "")
                                .slice(0, 10);
                              setFormData({
                                ...formData,
                                billing_phone_number: value,
                              });
                            }}
                            required={!formData.billing_same_as_shipping}
                            pattern="[0-9]{10}"
                            maxLength="10"
                            onBlur={(e) => {
                              if (
                                !formData.billing_same_as_shipping &&
                                e.target.value.length !== 10
                              ) {
                                Swal.fire({
                                  icon: 'none',
                                  imageUrl: "/shop/images/Failed_1.svg",
                                  imageWidth: '100%',
                                  imageHeight: 'auto',
                                  imageAlt: "Error",
                                  title: "Invalid Phone number",
                                  text: "phone number must be exactly 10 digits",
                                  confirmButtonColor: "#3085d6",
                                  confirmButtonText: "OK",
                                });
                              }
                            }}
                          />
                        </Form.Group>
                      </Col>

                      <Col>
                        <Form.Group>
                          <Form.Label>Pincode</Form.Label>
                          <Form.Control
                            type="text"
                            name="billing_pincode"
                            value={formData.billing_pincode || ""}
                            onChange={handleChange}
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  )}

                  <Row className="mb-3">
                    <Col>
                      <Form.Group>
                        <Form.Label>State</Form.Label>
                        <Form.Select
                          name="billing_state_id"
                          value={formData.billing_state_id}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select State</option>
                          {states.map((state) => (
                            <option key={state.id} value={state.id}>
                              {state.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group>
                        <Form.Label>District</Form.Label>
                        <Form.Select
                          name="billing_district_id"
                          value={formData.billing_district_id}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select District</option>
                          {districts.map((district) => (
                            <option key={district.id} value={district.id}>
                              {district.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <Form.Group>
                        <Form.Label>City</Form.Label>
                        <Form.Select
                          name="billing_city_id"
                          value={formData.billing_city_id}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select City</option>
                          {cities.map((city) => (
                            <option key={city.id} value={city.id}>
                              {city.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group>
                        <Form.Label>Zone</Form.Label>
                        <Form.Select
                          name="billing_zone_id"
                          value={formData.billing_zone_id}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select Zone</option>
                          {zones.map((zone) => (
                            <option key={zone.id} value={zone.id}>
                              {zone.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              )}
            </Form>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? "Saving..." : "Save Address"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const ComboDetailPage = ({ userId }) => {
  const { comboId, id } = useParams();
  const [comboDetails, setComboDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [selectedLeg, setSelectedLeg] = useState(null);
  const [availableLegs, setAvailableLegs] = useState([]);
  const [parentId, setParentId] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showReferralFullModal, setShowReferralFullModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [hasAddress, setHasAddress] = useState(false);

  const [customerData, setCustomerData] = useState({
    parent_id: id || "",
    leg: "0",
  });
  const [carouselPaused, setCarouselPaused] = useState(false);
  const carouselRef = useRef();

  const handleVideoPlay = () => {
    setCarouselPaused(true);
  };

  const handleVideoPause = () => {
    setCarouselPaused(false);
  };

  useEffect(() => {
    const authToken = localStorage.getItem("authToken");

    if (!authToken) {
      Swal.fire({
        icon: "warning",
        title: "Authentication Required",
        text: "Please log in to proceed.",
      }).then(() => {
        navigate("/shop/auths/login", { replace: true });
      });
    }
  }, [navigate]);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => console.log("Razorpay SDK Loaded");
    document.body.appendChild(script);

    const urlParams = new URLSearchParams(window.location.search);
    const legFromUrl = urlParams.get("leg");
    if (legFromUrl) {
      setCustomerData((prevData) => ({
        ...prevData,
        leg: legFromUrl,
      }));
    }
  }, []);

  const checkUserAddress = async () => {
    const authToken = localStorage.getItem("authToken");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_HOST}api/addres/getUserAddress`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      if (response.data.success) {
        setHasAddress(true);
        return true;
      } else {
        return false;
      }
    } catch (err) {
      console.error("Error checking address:", err);
      return false;
    }
  };

  const handleAddressSaved = async () => {
    setShowAddressModal(false);
    setHasAddress(true);

    // Automatically proceed with payment checks after address is saved
    await CheckUserPreviousorders();
  };

  const handlePayment = async (parent, leg) => {
    setError(null);
    try {
      setPaymentLoading(true);
      const authToken = localStorage.getItem("authToken");
      const response = await fetch(
        `${process.env.REACT_APP_HOST}api/payment/create-order?combo_id=${comboDetails.combo.id}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to create order");
      }

      const data = await response.json();

      if (!data.order_id || !data.razorpay_key || !data.amount) {
        throw new Error("Invalid payment details from the server");
      }

      const options = {
        key: data.razorpay_key,
        amount: data.amount,
        currency: data.currency,
        name: data.store_name,
        description: `Payment for ${comboDetails.combo.name}`,
        order_id: data.order_id,
        handler: async function (response) {
          await savePaymentSuccess(response, data, parent, leg);
        },
        prefill: {
          name: data.user?.name || "",
          email: data.user?.mail || "",
          contact: data.user?.phone || "",
        },
        theme: {
          color: "#3399cc",
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      setError(error.message);
    } finally {
      setPaymentLoading(false);
    }
  };

  const savePaymentSuccess = async (response, orderData, parent, leg) => {
    try {
      const authToken = localStorage.getItem("authToken");
      const saveResponse = await fetch(
        `${process.env.REACT_APP_HOST}api/user/BuyProduct`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            razorpay_order_id: orderData.order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            amount: orderData.amount,
            combo_id: orderData.combo_id,
            parent_id: parent,
            leg: leg,
            transaction_id: response.razorpay_payment_id,
          }),
        }
      );

      // if (!saveResponse.ok) {
      //   throw new Error('Failed to save payment success');
      // }
      if (!saveResponse.ok) {
        const errorData = await saveResponse.json(); // Parse the error response
        throw new Error(errorData.message);
      }

      const result = await saveResponse.json();

      // Replace alert with SweetAlert2

      await Swal.fire({
        imageUrl: "/shop/images/Payment.svg",
        imageWidth: '100%',
        imageHeight: 'auto',
        imageAlt: "Payment Done",
        title: "Payment Done",
        text: "Payment Done",
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
      });

      // Step 1: Navigate to Orders page
      navigate("/shop/auths/orders");

      // Step 2: Wait 3 seconds
      setTimeout(() => {
        // Step 3: Navigate to Add Bank Details page
        navigate("/shop/auths/user-bank-detail", {
          state: { message: "Payment successful!" },
        });
      }, 3000); // 3 seconds
    } catch (error) {
      // Also improve error handling with SweetAlert2
      await Swal.fire({
        icon: 'none',
        imageUrl: "/shop/images/Failed_1.svg",
        imageWidth: '100%',
        imageHeight: 'auto',
        imageAlt: "Error",
        text: error.message || "Failed to complete registration",
        confirmButtonColor: "#d33",
        confirmButtonText: "OK",
      });
      setError(error.message);
    }
  };
  const fetchAvailableLegs = async () => {
    setLoading(true);
    try {
      const authToken = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_HOST}api/usertree/check-available-space`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      const res = response.data;

      if (res.success && res.pass) {
        setParentId(res.parent_id);
        setSelectedLeg(res.leg);
        await handlePayment(res.parent_id, res.leg); // Added await here
        return;
      }

      if (res.success && !res.pass) {
        setAvailableLegs(res.all_available_legs || []);
        setParentId(res.parent_id);
        setSelectedLeg(res.suggested_leg?.toString() || null);
        setCustomerData((prev) => ({
          ...prev,
          parent_id: res.parent_id,
          leg: res.suggested_leg?.toString(),
        }));
        setShowModal(true);
        return;
      }

      if (!res.success) {
        setShowReferralFullModal(true);
      }
    } catch (err) {
      Swal.fire("Error", "Failed to fetch leg data.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleLegChange = (e) => {
    setSelectedLeg(e.target.value);
    setCustomerData({ ...customerData, leg: e.target.value });
  };

  const legLabels = {
    0: "Blue Team",
    1: "Yellow Team",
    2: "Green Team",
  };

  const handleConfirm = () => {
    if (!selectedLeg || !parentId) {
      Swal.fire("Error", "Please select a leg and try again.", "error");
      return;
    }

    setShowModal(false);
    handlePayment(parentId, selectedLeg);
  };

  const fetchComboDetails = async () => {
    try {
      const authToken = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_HOST}api/view-combo/${comboId}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      setComboDetails(response.data.data);

      // Check address after combo details are loaded
      const addressExists = await checkUserAddress();
      if (addressExists) {
        setHasAddress(true);
      }
    } catch (err) {
      console.error("Error fetching combo details:", err);
      setError("Failed to load combo details.");
    } finally {
      setLoading(false);
    }
  };

  const CheckUserPreviousorders = async () => {
    try {
      setLoading(true);
      const authToken = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_HOST}api/order/checkUserPreviousOrder`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.data.success) {
        await fetchAvailableLegs();
      } else {
        Swal.fire({
          imageUrl: "/shop/images/Failed_1.svg",
          imageWidth: '100%',
          imageHeight: 'auto',
          imageAlt: "Error",
          text: response.data.message,
        });
      }
    } catch (err) {
      console.error("Error checking previous orders:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to check your order history. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBuyNowClick = async () => {
    if (!hasAddress) {
      setShowAddressModal(true);
      return;
    }

    // Only proceed to payment checks if address exists
    CheckUserPreviousorders();
  };

  useEffect(() => {
    fetchComboDetails();
  }, [comboId]);

  if (loading) {
    return (
      <Layout title="Product Details">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '70vh' }}
        >
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">Loading Product Details...</p>
          </div>
        </div>
      </Layout>
    );
  }


  if (error) {
    return (
      <Layout title="Product Details">
        <div className="text-center">
          <Alert variant="danger">{error}</Alert>
        </div>
      </Layout>
    );
  }

  if (!comboDetails) {
    return (
      <Layout title="Product Details">
        <div className="text-center">
          <h5>No combo details available.</h5>
        </div>
      </Layout>
    );
  }

  const { combo, combo_images, combo_videos, combo_products } = comboDetails;

  return (
    <Layout title={combo.name}>
      <Row className="mt-4">
        <Col xs={12} md={4}>
          <div className="mt-0 ms-3 mb-4">
            <button
              className="btn btn-light d-flex align-items-center gap-2 shadow-sm border rounded-pill"
              onClick={() => navigate("/shop")}
              style={{
                padding: "6px 16px",
                fontWeight: 500,
                fontSize: "14px",
              }}
            >
              <span style={{ fontSize: "18px" }}>←</span>
              <span>Back</span>
            </button>
          </div>

          <Card className="shadow-sm border-0">
            <Card.Body className="h-100 shadow-sm border-0">
              <Carousel
                interval={carouselPaused ? null : 3000}
                indicators={true}
                controls={true}
                fade={false}
              >
                {combo_images.map((image, index) => (
                  <Carousel.Item key={`image-${index}`}>
                    <Card.Img
                      className="w-100 rounded-3"
                      src={`${process.env.REACT_APP_HOST}storage/${image.image}`}
                      alt={image.alt_tag || combo.name}
                      style={{ height: "380px", objectFit: "cover" }}
                    />
                  </Carousel.Item>
                ))}

                {combo_videos.map((video, index) => (
                  <Carousel.Item key={`video-${index}`}>
                    <video
                      className="w-100 rounded-3"
                      height="380"
                      controls
                      style={{ objectFit: "cover" }}
                      onPlay={() => setCarouselPaused(true)}
                      onPause={() => setCarouselPaused(false)}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <source
                        src={`${process.env.REACT_APP_HOST}storage/${video.video_url}`}
                        type="video/mp4"
                      />
                      Your browser does not support the video tag.
                    </video>
                    {video.alt_tag && (
                      <Carousel.Caption className="bg-dark bg-opacity-50 rounded-pill px-3 py-1 mt-2">
                        <small className="text-white">{video.alt_tag}</small>
                      </Carousel.Caption>
                    )}
                  </Carousel.Item>
                ))}

                {combo_images.length === 0 && combo_videos.length === 0 && (
                  <Carousel.Item>
                    <img
                      className="d-block w-100 rounded-3"
                      src="/path/to/default-image.jpg"
                      alt="Default Image"
                      style={{ height: "380px", objectFit: "cover" }}
                    />
                    <Carousel.Caption>
                      <h5>No media available.</h5>
                    </Carousel.Caption>
                  </Carousel.Item>
                )}
              </Carousel>

              <Row className="g-3 mt-4">
                <Col xs={12}>
                  <div className="text-center">
                    <button
                      className="btn btn-primary btn-lg shadow-sm rounded-pill px-4 py-2"
                      onClick={handleBuyNowClick}
                    >
                      Buy Now
                    </button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={8}>
          <Card className="p-3 shadow-sm rounded-6 mb-4 scrollable-card">
            <Card.Body>
              <Card.Title className="fs-4 fw-semibold text-primary mb-3  text-center">
                {combo.name}
              </Card.Title>

              <div
                className="mb-3"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(combo.description || ""),
                }}
              />

              <div className="d-flex justify-content-between align-items-center mb-4">
                <span className="fs-5 fw-bold">₹{combo.price}/-</span>
                <span className="badge bg-warning text-dark px-3 py-2 rounded-pill">
                  Best Deal
                </span>
              </div>

              <h5 className="mb-3">Included Products</h5>
              <div className="table-responsive">
                <table className="table table-hover table-bordered">
                  <thead className="table-light">
                    <tr>
                      <th>SL No</th>
                      <th>Product</th>
                      <th>Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {combo_products && combo_products.length > 0 ? (
                      combo_products.map((product, index) => (
                        <tr key={product.id}>
                          <td>{index + 1}</td>{" "}
                          {/* Display index starting from 1 */}
                          <td>{product.product_name}</td>
                          <td>{product.quantity}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center text-danger">
                          No products found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="d-grid mt-4">
                <button
                  className="btn btn-primary btn-lg shadow-sm"
                  onClick={handleBuyNowClick}
                >
                  Buy Now
                </button>
              </div>

              {/* Leg Selection Modal */}
              <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                centered
              >
                <Modal.Header closeButton>
                  <Modal.Title>Select Any One of them</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <div className="mb-3">
                    <label className="form-label fw-bold">Parent ID</label>
                    <input
                      type="text"
                      className="form-control"
                      readOnly
                      value={customerData.parent_id}
                    />
                  </div>
                  <label className="form-label fw-bold">Choose Any One</label>
                  <div className="d-flex gap-3 flex-wrap mt-2">
                    {["0", "1", "2"].map((leg) => (
                      <div key={leg} className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="leg"
                          id={`leg-${leg}`}
                          value={leg}
                          checked={selectedLeg === leg}
                          disabled={!availableLegs.includes(parseInt(leg))}
                          onChange={handleLegChange}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`leg-${leg}`}
                        >
                          {legLabels[leg]}
                        </label>
                      </div>
                    ))}
                  </div>
                </Modal.Body>
                <Modal.Footer>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleConfirm}
                    disabled={!selectedLeg}
                  >
                    Continue
                  </button>
                </Modal.Footer>
              </Modal>

              {/* Referral Full Modal */}
              <Modal
                show={showReferralFullModal}
                onHide={() => setShowReferralFullModal(false)}
                centered
              >
                <Modal.Header closeButton>
                  <Modal.Title>Referral Team Full</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center fs-6">
                  Your referral team is full. Do you want to build your own
                  team?
                </Modal.Body>
                <Modal.Footer>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setShowReferralFullModal(false)}
                  >
                    No
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setParentId(null);
                      setSelectedLeg(null);
                      setShowReferralFullModal(false);
                      handlePayment(null, null);
                    }}
                  >
                    Yes
                  </button>
                </Modal.Footer>
              </Modal>

              {/* Payment Loading Modal */}
              <Modal
                show={paymentLoading}
                centered
                backdrop="static"
                keyboard={false}
              >
                <Modal.Body className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <div className="mt-3 fw-medium">
                    Forwarding your payment, please wait...
                  </div>
                </Modal.Body>
              </Modal>
              <AddUserAddressModal
                show={showAddressModal}
                onHide={() => setShowAddressModal(false)}
                onSave={handleAddressSaved}
              />

              {/* Inline Address Modal (No import) */}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <br />
      <br />
      <br />
    </Layout>
  );
};

export default ComboDetailPage;
